/**
 * Diwali decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diwaliDecorationsPack: ToilePack = {
    slug: 'diwali-decorations',
    category: 'occasions',
    name: 'Diwali decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lanterns', label: 'Lanterns', subject: 'paper lanterns hung at a window'},
        {key: 'diyas', label: 'Diyas', subject: 'rows of diyas on a staircase'},
        {key: 'door', label: 'Door', subject: 'a door with a toran and rangoli'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'traditional decoration with clay diyas, marigold strings and flower petals, evening glow from below with warm flames, a dark warm balance with glowing highlights', palette: 'orange, yellow, red and brass gold', framing: 'eye-level close view, the decoration filling the frame'},
        {key: 'modern', label: 'Modern', style: 'modern minimal decoration with white candles, brass pieces and white flowers, soft dusk light from camera-left, a calm balance of cool blue dusk and warm candle points', palette: 'white, brass and soft blue', framing: 'straight-on view with clean empty space around'},
        {key: 'daytime', label: 'Daytime', style: 'festive decoration seen in daytime with marigold torans and fresh rangoli colours, bright daylight from overhead, a crisp bright balance with short shadows', palette: 'magenta, orange, yellow and green', framing: 'three-quarter view showing the whole setup'},
    ],
    directions: ['eco friendly', 'balcony', 'office desk'],
    directionHint: 'try: eco friendly',
    seo: {
        title: 'Diwali decoration: lanterns, diyas, door',
        metaDescription: 'Nine Diwali decoration pictures for an Indian home: paper lanterns, rows of diyas and a festive door, in traditional, modern and daytime looks.',
        h1: 'Diwali decoration: lanterns, diyas and a festive door',
        tagline: 'Nine pictures, three Diwali pieces, each in a traditional, a modern and a daytime look.',
        intro: 'Diwali decoration for an Indian home: paper lanterns hung at a window, rows of clay diyas on a staircase, and a door with a toran and rangoli. Each piece is shown in a traditional look of clay diyas, marigold strings and warm evening glow; a modern look of white candles, brass and soft blue dusk; and a daytime look of marigold torans and rangoli colours. Type a twist, such as eco friendly or balcony, and the same pieces carry it.',
        useCases: [
            'lanterns at a window for the festival',
            'diyas along a staircase',
            'a festive front door with a toran and rangoli',
            'a modern minimal setup with white candles',
        ],
        faq: [
            {q: 'What are the three looks?', a: 'Traditional, with clay diyas and marigold strings in evening glow; modern, with white candles and brass in soft dusk; and daytime, with marigold torans and bright rangoli colours.'},
            {q: 'Are the diyas and candles lit?', a: 'Yes. The traditional and modern looks show lit diyas or candles in evening light.'},
            {q: 'Can I ask for an eco friendly version?', a: 'Yes. Type a twist such as eco friendly, and the same pieces are shown with it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs. There are no people in them.'},
        ],
    },
}
